import React, { useRef, useState } from 'react';
import { Spinner, Typography } from '@zydalabs/zac-react';
import useTranslation from 'next-translate/useTranslation';
import { Icon } from '@zydalabs/storefront-components';
import { debounce } from 'common/utils';
import { IcSearch } from 'common/icons';
import ServiceConfigs from 'service/config';
import { usePersistedCountryDetails } from 'common/hooks/persistent';
import { searchOpenStreetMap } from './utils';
import { AutoCompleteInputWrapper, AutoCompleteItemWrapper, AutoCompleteListWrapper, SpinnerWrapper } from './style';

type MapTileTypes = {
  setCenter: (coords: [number, number]) => void;
};

const AutoComplete: React.FC<MapTileTypes> = ({ setCenter }) => {
  const [persistedCountryDetails] = usePersistedCountryDetails();
  const [isSearching, setIsSearching] = useState(false);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t, lang } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>();
  const multicountryEnabled = ServiceConfigs.getMulticountryEnabled();

  const handleSearch = debounce(async (value: string) => {
    if (!value) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    const results = await searchOpenStreetMap(value, lang, multicountryEnabled ? undefined : persistedCountryDetails?.code);
    setPredictions(results);

    setIsLoading(false);
  }, 800);

  const onPlaceSelected = (prediction: any) => {
    setCenter([parseFloat(prediction.lat), parseFloat(prediction.lon)]);
    setIsSearching(false);
  };

  return (
    <AutoCompleteInputWrapper
      tabIndex={0}
      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
        const { currentTarget } = event;
        requestAnimationFrame(() => {
          if (!currentTarget.contains(document.activeElement)) {
            setIsSearching(false);
          }
        });
      }}
    >
      <Icon icon={IcSearch} />
      <input
        ref={inputRef}
        placeholder={t('enterLocation')}
        onClick={() => setIsSearching(true)}
        onChange={evt => handleSearch(evt.target.value)}
      />
      {predictions.length > 0 && isSearching && (
        <AutoCompleteListWrapper>
          {isLoading ? (
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
          ) : (
            predictions.map(prediction => (
              <AutoCompleteItemWrapper key={prediction.place_id} onClick={() => onPlaceSelected(prediction)} aria-hidden="true">
                <Typography variant="body16" cursor="pointer">
                  {prediction.display_name}
                </Typography>
              </AutoCompleteItemWrapper>
            ))
          )}
        </AutoCompleteListWrapper>
      )}
    </AutoCompleteInputWrapper>
  );
};

export default AutoComplete;
